import { useStorage } from '@vueuse/core'
interface User {
  id?: number
  email?: string
  api_token?: string
  tenant_identifier?: string
  api_tokenV2?: string
  tenant_identifierV2?: string
  mid?: number
}
export const useUserStore = defineStore('userStore', () => {
  const toast = useToast()

  const { apiBaseUrl, apiBaseUrlV2 } = useAppConfig()
  const user = ref(useStorage('user', {} as User, localStorage))
  const calendarCategories = ref()
  const hasSupervisor = ref<boolean | null>(null)

  const contact = ref(
    useStorage('contact', {} as Contact & { token?: string; tenantId?: string }, localStorage)
  )

  const isLoggedIn = computed(() => !!user.value.api_token)
  const isLoggedInAsContact = computed(() => !!contact.value.token)
  const userEmail = computed(() => user.value.email ?? '-')
  const userProfilePicture = computed(() =>
    user?.value?.id
      ? `https:\\/\\/my.1tool.com\\/crm_files\\/users\\/${user.value.id}\\/logo.jpg`
      : 'https:\\/\\/my.1tool.com\\/images\\/standard_picture.jpg'
  )

  // Contact login with email and password
  async function contactLoginWithEmail(email: string, password: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'web'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response || !response.ok) {
      throw new Error(response?.status || '500')
    }

    const data = await response.json()
    if (data?.api_token) {
      contact.value.token = data.api_token
      contact.value.tenantId = tenantId
      await fetchContactProfile()
      return true
    }
    return false
  }

  /**
   * Exchange the token from a magic link for an API token.
   *
   * The mail carries `?magicLink=<token>`, which is a one-shot credential valid
   * for two hours — not an API token. It has to be handed to
   * `auth/contact/magic-link` to get one, which is the step that was missing:
   * the callback page read a query parameter the backend never sends.
   */
  async function contactLoginWithMagicLink(magicLink: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact/magic-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({ magicLink }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response || !response.ok) return false

    const data = await response.json()
    if (!data?.api_token) return false

    contact.value.token = data.api_token
    contact.value.tenantId = tenantId
    await fetchContactProfile()

    return true
  }

  // Request magic link for passwordless login
  async function requestMagicLink(email: string, link: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact/magic-link/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({ email, link }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    return response && response.ok
  }

  // Mail a four-digit PIN to reset a forgotten password. The backend answers
  // the same whether or not the address belongs to a contact, so a success
  // only means the request went through.
  async function requestPasswordReset(email: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact/password/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({ email }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => undefined)) as Response | undefined

    return !!response?.ok
  }

  // Set a new password with the PIN from the mail. A 422 is either a wrong or
  // expired PIN or a password the tenant's rule rejects; only the latter
  // carries a field error for `password`.
  async function resetPassword(
    email: string,
    pin: string,
    password: string
  ): Promise<'ok' | 'invalidPin' | 'weakPassword' | 'tooManyAttempts' | 'error'> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact/password/reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({ email, pin, password }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => undefined)) as Response | undefined

    if (!response) return 'error'
    if (response.ok) return 'ok'
    if (response.status === 429) return 'tooManyAttempts'
    if (response.status !== 422) return 'error'

    const data = await response.json().catch(() => ({}))
    return data?.errors?.password ? 'weakPassword' : 'invalidPin'
  }

  // Fetch contact profile after login
  async function fetchContactProfile(): Promise<void> {
    const profile = await useApi('css/profile', 'GET')
    if (profile?.data) {
      contact.value = { ...contact.value, ...profile.data }
    }
  }

  // Contact logout
  function contactLogout() {
    contact.value = {} as Contact & { token?: string; tenantId?: string }
    navigateTo('/login')
  }

  // Legacy user login (kept for reference)
  async function login(email: string, password: string): Promise<boolean> {
    const responseV2 = (await fetch(`${apiBaseUrlV2}auth/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'mac'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!responseV2.ok) {
      throw new Error(responseV2.status)
    }

    const response = (await fetch(`${apiBaseUrl}auth/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'mac'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response.ok) {
      throw new Error(response.status)
    }

    if (responseV2 && responseV2.status === 200 && response && response.status === 200) {
      const responseText = await response.text()
      const responseTextV2 = await responseV2.text()

      if (responseText && responseTextV2) {
        const { api_token: apiTokenV2, tenant_identifier: tenantIdentifierV2 } =
          JSON.parse(responseTextV2)
        const {
          api_token: apiToken,
          tenant_identifier: tenantIdentifier,
          id
        } = JSON.parse(responseText)

        user.value = {
          id,
          email,
          api_token: apiToken,
          tenant_identifier: tenantIdentifier,
          api_tokenV2: apiTokenV2,
          tenant_identifierV2: tenantIdentifierV2
        }

        if (!calendarCategories.value) await fetchCalendarCategories()

        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  function logout() {
    user.value = {}
    const contactValue = { ...contact.value }

    window?.localStorage?.clear()
    contact.value = contactValue
    navigateTo('/login')
  }

  async function fetchCalendarCategories() {
    if (!user.value.id) return
    try {
      const response = await useApi(`calendar-categories?filter[user_id]=${user.value.id}`, 'GET')
      calendarCategories.value = response?.data ?? []
    } catch {}
  }

  function setHasSupervisor(hasSupervisorParam: boolean | null) {
    hasSupervisor.value = hasSupervisorParam
  }

  return {
    user,
    userEmail,
    userProfilePicture,
    isLoggedIn,
    calendarCategories,
    hasSupervisor,
    contact,
    isLoggedInAsContact,
    fetchCalendarCategories,
    setHasSupervisor,
    login,
    logout,
    contactLoginWithEmail,
    requestMagicLink,
    contactLoginWithMagicLink,
    requestPasswordReset,
    resetPassword,
    fetchContactProfile,
    contactLogout
  }
})
// Use HMR for this store
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
